/**
 * Vesper Event Prompt Service (Shared)
 */

import apiClient from './api.client';
import { API } from '../constants/api';
import { deduped, bust, bustByPrefix } from './dedupedFetch';
import type {
  EventPrompt,
  EventPromptWithCandidates,
  GroupFormationSummary,
  CreateEventPromptPayload,
} from '../types/event_prompt.types';

export async function createEventPrompt(
  payload: CreateEventPromptPayload,
): Promise<EventPromptWithCandidates> {
  const res = await apiClient.post<{ success: true; data: EventPromptWithCandidates }>(
    API.EVENT_PROMPTS,
    payload,
  );
  // New prompt → busts every paginated key for the organizer's prompt list.
  bustByPrefix('me:eventPrompts:');
  return res.data.data;
}

export async function getMyEventPrompts(limit = 20, offset = 0): Promise<EventPrompt[]> {
  return deduped(`me:eventPrompts:limit=${limit}:offset=${offset}`, async () => {
    const res = await apiClient.get<{ success: true; data: EventPrompt[] }>(
      `${API.EVENT_PROMPTS}?limit=${limit}&offset=${offset}`,
    );
    return res.data.data;
  });
}

export async function getMyPendingScreenings(): Promise<EventPromptWithCandidates[]> {
  return deduped('me:pendingScreenings', async () => {
    const res = await apiClient.get<{ success: true; data: EventPromptWithCandidates[] }>(
      API.EVENT_PROMPT_SCREEN,
    );
    return res.data.data;
  });
}

export async function getEventPrompt(id: string): Promise<EventPromptWithCandidates> {
  const res = await apiClient.get<{ success: true; data: EventPromptWithCandidates }>(
    API.EVENT_PROMPT_BY_ID(id),
  );
  return res.data.data;
}

export async function respondToPromptScreening(
  id: string,
  accept: boolean,
): Promise<EventPromptWithCandidates> {
  const res = await apiClient.post<{ success: true; data: EventPromptWithCandidates }>(
    API.EVENT_PROMPT_RESPOND(id),
    { accept },
  );
  // Accept/Pass removes this prompt from my pending list and (if accept)
  // may surface it in my prompts list.
  bust('me:pendingScreenings');
  bustByPrefix('me:eventPrompts:');
  return res.data.data;
}

/** Organizer-only — set or replace the Zoom/Meet/Teams link on a remote
 *  room AFTER the Plan flow completed. Pass null or empty string to clear.
 *  Backend validates URL format + organizer ownership; throws on rejection. */
export async function updateZoomUrl(
  id: string,
  zoomUrl: string | null,
): Promise<{ zoomUrl: string | null }> {
  const res = await apiClient.patch<{ success: true; data: { zoomUrl: string | null } }>(
    API.EVENT_PROMPT_ZOOM_URL(id),
    { zoomUrl },
  );
  bustByPrefix('me:eventPrompts:');
  return res.data.data;
}

export async function convertPromptToEvent(id: string): Promise<EventPromptWithCandidates> {
  const res = await apiClient.post<{ success: true; data: EventPromptWithCandidates }>(
    API.EVENT_PROMPT_CONVERT(id),
  );
  bustByPrefix('me:eventPrompts:');
  return res.data.data;
}

export async function approveGroup(id: string): Promise<EventPromptWithCandidates> {
  const res = await apiClient.post<{ success: true; data: EventPromptWithCandidates }>(
    API.EVENT_PROMPT_APPROVE(id),
  );
  // Status flips to screening/confirmed; my prompts list shape changes.
  bustByPrefix('me:eventPrompts:');
  return res.data.data;
}

export async function swapCandidate(
  promptId: string,
  alternateUserId: string,
  primaryUserId: string,
): Promise<EventPromptWithCandidates> {
  const res = await apiClient.post<{ success: true; data: EventPromptWithCandidates }>(
    API.EVENT_PROMPT_SWAP(promptId),
    { alternateUserId, primaryUserId },
  );
  return res.data.data;
}

export async function getGroupStatus(id: string): Promise<GroupFormationSummary> {
  const res = await apiClient.get<{ success: true; data: GroupFormationSummary }>(
    API.EVENT_PROMPT_GROUP_STATUS(id),
  );
  return res.data.data;
}

export async function cancelEventPrompt(
  id: string,
  reason: 'scrapped_plan' | 'rejected_group' | 'organizer_cancel' = 'organizer_cancel',
): Promise<void> {
  await apiClient.post(API.EVENT_PROMPT_CANCEL(id), { reason });
  bustByPrefix('me:eventPrompts:');
  bust('me:pendingScreenings');
}

export async function partialRematch(promptId: string): Promise<EventPromptWithCandidates> {
  const { data } = await apiClient.post(`${API.EVENT_PROMPTS}/${promptId}/partial-rematch`);
  return data;
}

/** Bench friend who is NOT already a candidate on the given prompt. Returned
 *  by GET /events/prompts/:id/bench-friends — used to populate the matching-
 *  screen "Add from your bench" picker. */
export interface BenchFriendForPrompt {
  userId: string;
  displayName: string;
  avatarUrl: string | null;
  trustScore: number;
  isBenchFriend: boolean;
}

/** Adds a friend from the organizer's network (friend_bench) as an ALTERNATE
 *  on the prompt. Organizer then uses the existing "Swap into lineup" button
 *  on the resulting alternate card to promote them — single-step add flow.
 *
 *  The new candidate row is marked `manuallyAdded` so the UI can render a
 *  distinct "Added by you" chip instead of an algorithmic-fit chip.
 *
 *  Constraints enforced server-side:
 *   - caller must be the organizer
 *   - prompt must be in 'proposed' state
 *   - networkUserId must be in the organizer's network (friend_bench)
 *   - networkUserId must not already be a candidate */
export async function addNetworkCandidate(
  promptId: string,
  networkUserId: string,
): Promise<EventPromptWithCandidates> {
  const res = await apiClient.post<{ success: true; data: EventPromptWithCandidates }>(
    `${API.EVENT_PROMPTS}/${promptId}/add-network-candidate`,
    { networkUserId },
  );
  return res.data.data;
}

export async function getBenchFriendsForPrompt(
  promptId: string,
): Promise<BenchFriendForPrompt[]> {
  const res = await apiClient.get<{ success: true; data: BenchFriendForPrompt[] }>(
    `${API.EVENT_PROMPTS}/${promptId}/bench-friends`,
  );
  return res.data.data;
}
